import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  Home, 
  Package, 
  MapPin, 
  FileText, 
  Users, 
  Wallet, 
  User, 
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { useState } from 'react';
import ThemeSwitcher from '../theme/ThemeSwitcher';
import ThemeTest from '../theme/ThemeTest';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Parcels', href: '/parcels', icon: Package },
    { name: 'Trips', href: '/trips', icon: MapPin },
    { name: 'Applications', href: '/applications', icon: FileText },
    { name: 'Agreements', href: '/agreements', icon: Users },
    { name: 'Wallet', href: '/wallet', icon: Wallet },
    { name: 'Profile', href: '/profile', icon: User },
  ];

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
    setShowLogoutModal(false);
  };

  const getVerificationBadge = () => {
    if (!user) return null;
    
    const badges = {
      unverified: { 
        className: 'bg-theme-error-100 text-theme-error-800', 
        text: 'Unverified' 
      },
      partial: { 
        className: 'bg-theme-warning-100 text-theme-warning-800', 
        text: 'Pending' 
      },
      verified: { 
        className: 'bg-theme-success-100 text-theme-success-800', 
        text: 'Verified' 
      },
    };

    const badge = badges[user.verification_status] || badges.unverified;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen flex bg-theme-background">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-500 ease-in-out lg:static lg:inset-0 bg-theme-card ${!sidebarOpen && 'lg:translate-x-0 lg:w-16'} ${sidebarOpen && 'lg:translate-x-0 lg:w-64'}`}>
        <div className="flex items-center justify-between h-16 px-6 border-theme-light border-b">
          <h1 className={`${!sidebarOpen && 'lg:hidden'} text-xl font-bold`}>
            <span className="text-theme-primary">Trip</span>
            <span className="text-theme-secondary">Trot</span>
          </h1>
          <div className="flex items-center space-x-2">
            
            <button
              onClick={handleLogout}
              className={`${!sidebarOpen && 'lg:hidden'} flex items-center w-full px-3 py-2 text-sm font-medium rounded-md text-theme-secondary hover:bg-theme-neutral-100 hover:text-theme-primary transition-colors`}
            >
              <LogOut className="mr-3 h-4 w-4" />
              {/* Logout */}
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-theme-secondary hover:text-theme-primary transition-colors items-center"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        
        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex ${sidebarOpen ? 'px-3 py-2' : 'lg:px-3 lg:py-2 lg:justify-start'} text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-theme-primary-50 text-theme-primary border-r-2 border-theme-primary'
                      : 'text-theme-secondary hover:bg-theme-neutral-100 hover:text-theme-primary'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  title={!sidebarOpen ? item.name : undefined}
                >
                  <item.icon
                    className={`${sidebarOpen ? 'mr-3' : 'lg:mr-0'} h-5 w-5 ${
                      isActive ? 'text-theme-primary' : 'text-theme-tertiary group-hover:text-theme-primary'
                    }`}
                  />
                  <span className={`${!sidebarOpen && 'lg:hidden'}`}>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-500 ease-in-out ${sidebarOpen ? 'lg:ml-0' : 'lg:ml-16'}`}>
        {/* Top bar - shows on desktop when sidebar is closed */}
        {/* {!sidebarOpen && (
          <div className="hidden lg:flex lg:items-center lg:justify-between lg:px-6 lg:py-4 lg:bg-theme-card lg:border-theme-light lg:border-b">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-theme-secondary hover:text-theme-primary transition-colors mr-4"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-xl font-bold flex items-center">
                <span className="text-theme-primary">Trip</span>
                <span className="text-theme-secondary">Trot</span>
              </h1>
            </div>
            <ThemeSwitcher />
          </div>
        )} */}

        {/* Mobile top bar */}
        <div className="lg:hidden">
          <div className="flex items-center h-16 px-4 bg-theme-card border-theme-light border-b">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-theme-secondary hover:text-theme-primary transition-colors"
            >
              <Menu className="h-6 w-6 m-auto" />
            </button>

            <h1 className="text-xl ml-2 font-bold flex items-center text-theme-primary">
              <span className="text-theme-primary">Trip</span>
              <span className="text-theme-secondary">Trot</span>
            </h1>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          {/* <ThemeTest /> */}
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-md w-full border-theme-light border">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-theme-error rounded-full mb-4">
                <LogOut className="h-6 w-6 text-theme-inverse" />
              </div>
              <h3 className="text-lg font-medium text-theme-primary text-center mb-2">
                Sign Out of Your Account?
              </h3>
              <p className="text-sm text-theme-secondary text-center mb-6">
                Are you sure you want to sign out? You'll need to sign in again to access your account.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 px-4 py-2 border-theme-light border text-theme-secondary rounded-md hover:bg-theme-neutral-100"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="flex-1 px-4 py-2 bg-theme-error text-theme-inverse rounded-md hover:bg-theme-error-hover"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
